import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { ChatComposer } from '../components/ChatComposer';
import { ChatSuggestedPrompts } from '../components/ChatSuggestedPrompts';
import { ChatMessageList } from '../components/ChatMessageList';
import { ChatThreadList } from '../components/ChatThreadList';
import { GlobalButton } from '../components/GlobalButton';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { useTheme } from '../context/ThemeProvider';
import { askIntelligence, askIntelligenceErrorMessage } from '../lib/askIntelligence';
import { sortChatMessagesChronological, useChatThreads } from '../hooks/useChatThreads';
import type { ChatMessage } from '../types';
import authStyles from '../pages/auth/authPages.module.css';
import composerStyles from '../components/ChatSuggestedPrompts.module.css';
import styles from './IntelligenceView.module.css';

const PENDING_ASSISTANT_ID = 'pending-assistant';

function buildOptimisticUserMessage(
  userId: string,
  threadId: string,
  content: string
): ChatMessage {
  return {
    id: `optimistic-user-${Date.now()}`,
    userId,
    threadId,
    role: 'user',
    content,
    createdAt: new Date().toISOString(),
  };
}

function buildPendingAssistantMessage(userId: string, threadId: string): ChatMessage {
  return {
    id: PENDING_ASSISTANT_ID,
    userId,
    threadId,
    role: 'assistant',
    content: 'Thinking…',
    createdAt: new Date().toISOString(),
  };
}

export function IntelligenceView() {
  const [searchParams] = useSearchParams();
  if (searchParams.get('view') === 'scenarios') {
    return <Navigate to="/scenarios" replace />;
  }
  return <IntelligenceChatView />;
}

function IntelligenceChatView() {
  const { user } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [optimisticUser, setOptimisticUser] = useState<ChatMessage | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);
  const [composerDraft, setComposerDraft] = useState('');

  const chat = useChatThreads(user?.uid ?? '', activeThreadId);

  useEffect(() => {
    if (activeThreadId || chat.threadsLoading || chat.threads.length === 0) return;
    setActiveThreadId(chat.threads[0].id);
  }, [activeThreadId, chat.threads, chat.threadsLoading]);

  useEffect(() => {
    if (!optimisticUser) return;

    const persisted = chat.messages.some(
      (message) => message.role === 'user' && message.content === optimisticUser.content
    );
    if (persisted) {
      setOptimisticUser(null);
    }
  }, [chat.messages, optimisticUser]);

  const handleNewChat = useCallback(async () => {
    setCreating(true);
    setActionError(null);

    try {
      const thread = await chat.createThread();
      setActiveThreadId(thread.id);
      setMobileSidebarOpen(false);
      setSendError(null);
      setRetryMessage(null);
      setOptimisticUser(null);
      setComposerDraft('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not start a new chat');
    } finally {
      setCreating(false);
    }
  }, [chat]);

  const handleSelectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setMobileSidebarOpen(false);
    setSendError(null);
    setRetryMessage(null);
    setOptimisticUser(null);
    setComposerDraft('');
  }, []);

  const activeThread = chat.threads.find((thread) => thread.id === activeThreadId) ?? null;

  const handleDeleteActiveChat = useCallback(async () => {
    if (!activeThreadId || !user?.uid || deleting) return;

    const title = activeThread?.title ?? 'this chat';
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;

    setDeleting(true);
    setActionError(null);
    setSendError(null);
    setRetryMessage(null);
    setOptimisticUser(null);

    const deletedId = activeThreadId;

    try {
      await chat.deleteThread(deletedId);
      const nextThread = chat.threads.find((thread) => thread.id !== deletedId);
      setActiveThreadId(nextThread?.id ?? null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not delete chat');
    } finally {
      setDeleting(false);
    }
  }, [activeThread?.title, activeThreadId, chat, deleting, user?.uid]);

  const displayMessages = useMemo(() => {
    const userId = user?.uid ?? '';
    const threadId = activeThreadId ?? '';
    let items = chat.messages;

    if (optimisticUser) {
      const hasPersisted = items.some(
        (message) =>
          message.role === 'user' &&
          message.content === optimisticUser.content &&
          message.id !== optimisticUser.id
      );
      if (!hasPersisted) {
        items = [...items, optimisticUser];
      }
    }

    if (sending && userId && threadId) {
      items = [...items, buildPendingAssistantMessage(userId, threadId)];
    }

    return sortChatMessagesChronological(items);
  }, [activeThreadId, chat.messages, optimisticUser, sending, user?.uid]);

  const visibleMessageCount = useMemo(
    () =>
      displayMessages.filter((message) => message.role === 'user' || message.role === 'assistant')
        .length,
    [displayMessages]
  );

  const handleComposerSend = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      const userId = user?.uid;
      if (!userId || !activeThreadId || !trimmed || sending) return;

      setSendError(null);
      setRetryMessage(null);
      setComposerDraft('');
      setOptimisticUser(buildOptimisticUserMessage(userId, activeThreadId, trimmed));
      setSending(true);

      try {
        await askIntelligence(activeThreadId, trimmed);
        setOptimisticUser(null);
      } catch (err) {
        setOptimisticUser(null);
        setSendError(askIntelligenceErrorMessage(err));
        setRetryMessage(trimmed);
        setComposerDraft(trimmed);
      } finally {
        setSending(false);
      }
    },
    [activeThreadId, sending, user?.uid]
  );

  const handleComposerSubmit = useCallback(() => {
    void handleComposerSend(composerDraft);
  }, [composerDraft, handleComposerSend]);

  const handleRetrySend = useCallback(() => {
    if (!retryMessage) return;
    void handleComposerSend(retryMessage);
  }, [handleComposerSend, retryMessage]);

  const toolbarActions = (
    <>
      <GlobalButton
        type="button"
        variant="secondary"
        autoWidth
        className={styles.mobileToggle}
        onClick={() => setMobileSidebarOpen(true)}
        aria-label="Show chat list"
      >
        Chats
      </GlobalButton>
      <GlobalButton
        type="button"
        variant="secondary"
        autoWidth
        isLoading={creating}
        disabled={creating}
        onClick={handleNewChat}
        icon={
          <img
            src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        New chat
      </GlobalButton>
      <GlobalButton
        type="button"
        variant="secondary"
        destructive
        autoWidth
        aria-label={
          activeThread ? `Delete chat: ${activeThread.title}` : 'Delete chat'
        }
        disabled={!activeThreadId || deleting}
        isLoading={deleting}
        onClick={() => void handleDeleteActiveChat()}
        icon={<img src="/assets/icons/trash.red.svg" alt="" width={16} height={16} />}
      >
        Delete chat
      </GlobalButton>
    </>
  );

  return (
    <ViewPage title="Intelligence" actions={toolbarActions}>
      <div className={styles.layout}>
        {mobileSidebarOpen ? (
          <button
            type="button"
            className={styles.overlay}
            aria-label="Close chat list"
            onClick={() => setMobileSidebarOpen(false)}
          />
        ) : null}

        <div className={`${styles.threadColumn} ${mobileSidebarOpen ? styles.threadColumnOpen : ''}`}>
          <div className={styles.drawerTopBar}>
            <GlobalButton
              type="button"
              variant="secondary"
              iconOnly
              autoWidth
              className={styles.drawerClose}
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Close chat list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </GlobalButton>
          </div>
          <div
            style={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minHeight: 0,
              padding: '0',
            }}
          >
            <ChatThreadList
              threads={chat.threads}
              activeThreadId={activeThreadId}
              loading={chat.threadsLoading}
              error={chat.error ?? actionError}
              creating={creating}
              onSelectThread={handleSelectThread}
              onNewChat={handleNewChat}
            />
          </div>
        </div>

        <main className={styles.main} aria-label="Chat conversation">
          <div className={styles.sectionHead}>
            <ViewSectionHeader
              eyebrow="Portfolio chat"
              title={activeThread?.title ?? 'Intelligence'}
              subtitle="Ask questions about your net worth, investments, bank activity, and deal pipeline — grounded in your Perfina data."
            />
          </div>

          {activeThread ? (
            <div className={styles.conversation}>
              <div className={styles.messagesScroll}>
                <ChatMessageList messages={displayMessages} loading={chat.messagesLoading} />

                {sendError ? (
                  <div
                    className={`${styles.sendError} ${authStyles.banner} ${authStyles.bannerError}`}
                    role="alert"
                  >
                    <p style={{ margin: 0 }}>{sendError}</p>
                    <GlobalButton
                      type="button"
                      variant="secondary"
                      autoWidth
                      className={styles.sendErrorButton}
                      disabled={sending}
                      isLoading={sending}
                      onClick={handleRetrySend}
                    >
                      Try again
                    </GlobalButton>
                  </div>
                ) : null}
              </div>

              <div className={styles.composerDock}>
                <div className={composerStyles.footer}>
                  {visibleMessageCount === 0 ? (
                    <ChatSuggestedPrompts onSelect={setComposerDraft} disabled={sending} />
                  ) : null}
                  <ChatComposer
                    value={composerDraft}
                    onChange={setComposerDraft}
                    onSend={handleComposerSubmit}
                    loading={sending}
                  />
                </div>
              </div>
            </div>
          ) : (
            <p className={`${styles.mainPlaceholder} muted`}>
              Select a chat from the sidebar or start a new conversation.
            </p>
          )}
        </main>
      </div>
    </ViewPage>
  );
}
